import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Stack, Text } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import { downloadBlob } from '@repletoryreach/sdk-utils/file';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount, formatDateTime } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { flowNames, rowsCsv, type Row } from '../lib/data';
import { MUTED, Muted, Panel, channelLabel, outcomeLabel, sentimentLabel } from '../shared';
import type { TabProps } from './tab';

const PAGE = 50;

/** Every chat the filters leave, newest first, each opening its conversation in the inbox, and the lot as a CSV. */
export function Chats({ report, rows, tz }: TabProps) {
  const t = useT();
  const [page, setPage] = useState(1);
  const sorted = [...rows].sort((a, b) => b.at.localeCompare(a.at));
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const current = Math.min(page, pages);
  const names = flowNames(report);
  const download = () => downloadBlob(new Blob([rowsCsv(sorted, t, tz, names)], { type: 'text/csv;charset=utf-8' }), `insights-${report.from}-${report.to}-chats.csv`);

  return (
    <Panel title={t('insights.chats.title')} note={t('insights.chats.note', { count: formatCount(rows.length) })} testId="insights-chats"
      actions={<Button size="xs" variant="default" onClick={download} disabled={rows.length === 0} data-testid="insights-csv">{t('insights.chats.csv')}</Button>}>
      {rows.length === 0 ? <Muted>{t('insights.empty.filtered')}</Muted> : (
        <div className="rr-insights__table-scroll">
          <DataTable<Indexed<Row>>
            records={sorted.slice((current - 1) * PAGE, current * PAGE) as Indexed<Row>[]}
            idAccessor="ref"
            page={current}
            totalRecords={sorted.length}
            recordsPerPage={PAGE}
            onPageChange={setPage}
            columns={[
              { accessor: 'ref', title: t('insights.chats.ref'), render: (r) => <Text role="caption" c={MUTED}>{r.ref}</Text> },
              { accessor: 'at', title: t('insights.chats.date'), render: (r) => <Text role="caption">{formatDateTime(r.at)}</Text> },
              { accessor: 'contact_name', title: t('insights.chats.person'), render: (r) => (
                <Stack gap={0}>
                  <Text role="bodySmall" fw={600}>{r.contact_name ?? t('insights.chats.unknown')}</Text>
                  <Text role="caption" c={MUTED}>{channelLabel(t, r.channel)}{r.customer_type ? ` · ${r.customer_type}` : ''}</Text>
                </Stack>
              ) },
              { accessor: 'summary', title: t('insights.chats.summary'), render: (r) => (
                <Stack gap={0}>
                  <Text role="bodySmall">{r.read ? r.summary : t('insights.chats.notRead')}</Text>
                  {r.topics.length > 0 && <Text role="caption" c={MUTED}>{r.topics.join(', ')}</Text>}
                  {r.flows.length > 0 && (
                    <Text role="caption" c={MUTED}>{t('insights.chats.flows', { names: r.flows.map((id) => names.get(id) ?? t('insights.chats.unknown')).join(', ') })}</Text>
                  )}
                  {r.quote && <Text role="caption" c={MUTED}>“{r.quote}”</Text>}
                </Stack>
              ) },
              { accessor: 'outcome', title: t('insights.chats.outcome'), render: (r) => (
                <Stack gap={0}>
                  <Text role="caption">{r.outcome ? outcomeLabel(t, r.outcome) : ''}</Text>
                  {r.sentiment && <Text role="caption" c={MUTED}>{sentimentLabel(t, r.sentiment)}</Text>}
                  {r.stop_reason && <Text role="caption" c={MUTED}>{r.stop_reason}</Text>}
                </Stack>
              ) },
              { accessor: 'conversation_id', title: '', render: (r) => (
                <Link to={`/inbox?conversation=${encodeURIComponent(r.conversation_id)}`} data-testid="insights-open-chat">
                  <Text role="caption">{t('insights.chats.open')}</Text>
                </Link>
              ) }
            ]}
          />
        </div>
      )}
    </Panel>
  );
}
